<template lang="pug">
.study-question-horizontal
  //- タイトル部
  .study-question-horizontal__title
    .study-question-horizontal__title__text.font-bold.text-large
      .study-question-horizontal__title__text__problem-number
        .study-question-horizontal__title__text__problem-number__label
          slot(name='problemNumberBeforeLabel')
        .study-question-horizontal__title__text__problem-number__value {{ problemNumber }}
        .study-question-horizontal__title__text__problem-number__label
          slot(name='problemNumberAfterLabel')
      .study-question-horizontal__title__text__question-number {{ questionNumberText }}
    pen-selector.study-question-horizontal__title__pen-selector(ref='penSelector', @updated='changeActivePen')
  //- 音声プレイヤー部
  .study-question-horizontal__audio(v-if='audioUrl')
    image-base.study-question-horizontal__audio__comment-image(:src='audioCommentImageUrl')
    audio-player.study-question-horizontal__audio__player(
      :src='audioUrl',
      :playCount='audioPlayCount',
      :showIndicator='audioViewSetting.showIndicator',
      :availablePause='audioViewSetting.availablePause',
      :availableReplay='audioViewSetting.availableReplay',
      @started='audioStartedEvent'
    )
  //- 問題表示部
  .study-question-horizontal__content(v-if='!isChangingProblem')
    .study-question-horizontal__content__question(ref='questionBlock')
      image-base.study-question-horizontal__content__question__image(
        v-for='(imageUrl, index) in imageUrls',
        :src='imageUrl',
        :key='index',
        @load='adjustAndRestoreCanvas'
      )
      pen-canvas.study-question-horizontal__content__question__canvas(
        ref='penCanvas',
        :pen='activePen',
        mode='horizontal',
        :drawingData='questionDrawingData',
        @draw-end='drawEndEvent'
      )
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator'
import PenSelector from '@/components/organisms/student/PenSelector.vue'
import ImageBase from '@/components/atoms/ImageBase.vue'
import AudioPlayer from '@/components/molecules/student/AudioPlayer.vue'
import PenCanvas from '@/components/molecules/student/PenCanvas.vue'
import { DrawSettings } from '@/models/drawSettings'
import { DrawingDataType } from '@/models/drawingDataModel'
import { AudioViewSetting } from '@/const/AudioViewTypes'
import { debounce } from 'lodash'

/**
 * 学習画面問題表示エリア（横書き）
 */
@Component({
  components: {
    PenSelector,
    ImageBase,
    AudioPlayer,
    PenCanvas,
  },
})
export default class StudyQuestionHorizontal extends Vue {
  // 問題切替中フラグ
  @Prop()
  isChangingProblem!: boolean

  // 問題番号
  @Prop()
  problemNumber!: number

  // 設問番号テキスト
  @Prop()
  questionNumberText!: string

  // 音声URL
  @Prop()
  audioUrl?: string

  // 音声再生回数
  @Prop()
  audioPlayCount?: number

  // 音声コメント画像URL
  @Prop()
  audioCommentImageUrl!: string

  // 音声表示設定
  @Prop()
  audioViewSetting!: AudioViewSetting

  // 問題画像URL群
  @Prop()
  imageUrls!: string[]

  // 音声再生時イベント
  @Prop()
  audioStartedEvent!: Function

  // 描画終了時イベント
  @Prop()
  drawEndEvent!: Function

  // 描画データ
  @Prop()
  questionDrawingData?: DrawingDataType | null

  // ペン選択のRef
  @Ref()
  penSelector!: PenSelector

  // ペンキャンバスのRef
  @Ref()
  penCanvas!: PenCanvas

  @Ref()
  questionBlock!: HTMLElement

  private debouncedHandleResize = debounce(this.adjustAndRestoreCanvas, 50)

  mounted() {
    window.addEventListener('resize', this.debouncedHandleResize)

    this.$nextTick(() => {
      setTimeout(() => {
        this.changeCanvasSize()
      }, 0)
    })
  }

  private beforeDestroy() {
    window.removeEventListener('resize', this.debouncedHandleResize)
  }

  private activePen: DrawSettings.PenAsset | null = null

  private changeActivePen() {
    this.activePen = this.penSelector?.activePen || null
  }

  private adjustAndRestoreCanvas() {
    this.changeCanvasSize()
  }

  /**
   * isChangingProblemを監視して再描画
   * - immediate: trueにより、マウント時にも実行される
   * - isChangingがfalseになったタイミングで実行することで、
   *   propsの更新とDOMの反映が完了した状態でcanvasを初期化できる
   */
  @Watch('isChangingProblem', { immediate: true })
  private async onIsChangingProblemChanged(isChanging: boolean): Promise<void> {
    if (!isChanging) {
      await this.$nextTick()
      this.changeCanvasSize()
    }
  }

  private restoreDrawingData() {
    if (this.penCanvas) {
      // 既存の描画データがあれば復元、なければクリアする
      this.penCanvas.restoreDrawingData(this.questionDrawingData || null)
    }
  }

  // canvasサイズ設定
  private setCanvas(size: { width: number; height: number } | undefined): void {
    if (!this.penCanvas.canvas || !this.penCanvas.overlayCanvas || !size) return

    this.penCanvas.canvas.setCanvasSize(size.width, size.height)
    this.penCanvas.overlayCanvas.setCanvasSize(size.width, size.height)
  }

  private calculateCanvasSize(element: HTMLElement | undefined): { width: number; height: number } | undefined {
    if (!element) return undefined
    // ブラウザのサイズ変更時、問題画像のサイズ変更にキャンバスが追随しないため、heightをelement.clientWidthに設定
    return { width: element.clientWidth, height: element.clientWidth }
  }

  /**
   * canvasサイズ変更時、出題エリアサイズ変更時の処理
   * このメソッドがcanvasのサイズ設定と描画データ復元の両方を担当する
   */
  private changeCanvasSize(): void {
    if (!this.penCanvas || !this.questionBlock) {
      return
    }

    // canvasサイズ設定
    const newSize = this.calculateCanvasSize(this.questionBlock)
    this.setCanvas(newSize)

    // 描画データを復元（スケーリングもここで行われる）
    this.restoreDrawingData()
  }
}
</script>

<style lang="stylus" scoped>
.study-question-horizontal
  display flex
  flex-direction column
  border 2px solid var(--color-study-content__border)
  background-color var(--color-study-content__background)
  border-radius 4px
  padding 10px 20px

  &__title
    display flex
    align-items center
    justify-content space-between
    color var(--color-study-content-title__text)
    background-color var(--color-study-content-title__background)
    padding 5px 20px
    margin-bottom 10px
    border-radius 10px
    &__text
      display flex
      &__problem-number
        display flex

  &__audio
    display flex
    justify-content flex-start
    align-items flex-start
    flex-direction column
    &__comment-image
      max-width 100%
    &__player
      display flex

  &__content
    overflow-y scroll
    max-width 100%
    height 100%
    &__question
      position relative
      width 100%
      height 100%
      &__canvas
        position absolute
        width 100%
        height 100%
        top 0
        left 0
</style>
